import { useEffect, useRef, useState } from 'react';

import { clearSession, currentSession } from '../lib/session.js';
import './page-header.css';

/**
 * The portal's chrome: who this is, where in the exercise you are, and who is signed in.
 *
 * The step indicator is text first and a rule second — a person should be able to answer "how much
 * is left" without decoding a graphic, and a screen reader gets the same sentence rather than a row
 * of unlabelled dots. The account menu, top right, shows the signed-in email and offers the one
 * thing it needs to: signing out.
 */

export interface Step {
  readonly key: string;
  readonly label: string;
}

export interface PageHeaderProps {
  readonly steps: readonly Step[];
  readonly currentKey: string;
}

function signOut(): void {
  clearSession();
  window.location.assign('/login');
}

export function PageHeader({ steps, currentKey }: PageHeaderProps) {
  const index = steps.findIndex((step) => step.key === currentKey);
  const current = index < 0 ? 0 : index;
  const session = currentSession();

  return (
    <header className="masthead">
      <div className="masthead__inner">
        <p className="masthead__brand">Tooth Somatics</p>

        <nav className="masthead__steps" aria-label="Your progress">
          <p className="sr-only">
            Step {current + 1} of {steps.length}: {steps[current]?.label}
          </p>
          <ol className="masthead__list">
            {steps.map((step, position) => (
              <li
                key={step.key}
                className={[
                  'masthead__step',
                  position === current ? 'masthead__step--current' : '',
                  position < current ? 'masthead__step--done' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-hidden="true"
              >
                <span className="masthead__step-number">{position + 1}</span>
                <span className="masthead__step-label">{step.label}</span>
              </li>
            ))}
          </ol>
        </nav>

        {session ? <AccountMenu email={session.email} /> : null}
      </div>
    </header>
  );
}

/**
 * The signed-in person and a way out.
 *
 * A button that opens a small menu, closed by a click elsewhere or the Escape key — the ordinary
 * behaviour of a menu, so it needs no explanation. The initial in the circle is a quiet stand-in
 * for an avatar the product does not collect.
 */
function AccountMenu({ email }: { email: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const initial = (email.trim()[0] ?? '?').toUpperCase();

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="account" ref={ref}>
      <button
        type="button"
        className="account__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="account__avatar" aria-hidden="true">
          {initial}
        </span>
        <span className="account__email">{email}</span>
      </button>

      {open ? (
        <div className="account__menu" role="menu">
          <p className="account__signed-in">Signed in as</p>
          <p className="account__menu-email">{email}</p>
          <button type="button" className="account__signout" role="menuitem" onClick={signOut}>
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
